import type { FC } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { useGetMyRegionEvents } from '@/features/api/hooks/grapevine-post/useGetMyRegionEvents';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { FeatureGuard } from '@/features/feature-management/components/FeatureGuard';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { RegionTab } from '@/features/geo-area/components/region/regionTypes';
import { useRegionalfunkRadiusStore } from '@/features/geo-area/store/useRegionalfunkRadiusStore';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { HappeningCard } from '@/features/start/components/radio/HappeningCard';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';

const styles = StyleSheet.create({
  loading: { paddingVertical: 24, alignItems: 'center' }
});

export const RegionHappeningsSection: FC = memo(() => {
  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'screens.start.sections.radio'
  });

  const homeAreaId = useGetPerson().data?.homeAreaId;
  const radiusKm = useRegionalfunkRadiusStore((s) => s.meineRegionRadiusKm);
  const happeningQuery = useGetMyRegionEvents({
    radiusKm,
    homeAreaId,
    count: 1
  });
  const { data: happenings, isLoading } = happeningQuery;
  useRefetchOnFocus(happeningQuery);

  const happening = happenings?.[0];

  if (!isLoading && !happening) {
    return;
  }

  return (
    <FeatureGuard features={[FeaturesEnum.Happening]}>
      <SectionContent
        title={t('happeningsRegion')}
        testId="section-region-happenings"
        screen={AppRoute.Tabs}
        params={{ screen: TabRoute.Region, params: { tab: RegionTab.EVENTS } }}
      >
        {isLoading || !happening ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" />
          </View>
        ) : (
          <HappeningCard {...happening} />
        )}
      </SectionContent>
    </FeatureGuard>
  );
});

RegionHappeningsSection.displayName = 'RegionHappeningsSection';
